import React, { useState, useEffect } from "react";
import { AppLayout } from "../../templates";
import {
    AppPopup,
    IEDistributionList,
    IEDistributionListFilters,
    IEDistributionAddUpdate,
    IEDistributionDelete
} from "../../components";
import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { ieDistributionActions, alertActions, shiftActions } from "../../_redux/actions";
import { authorizationService } from "../../_services";
import {
    AddUpdateIEDistributionPayloadStructure,
    DeleteIEDistributionPayloadStructure,
    IEDistributionListFilterStructure
} from "../../_models/app.models";
import {
    IEDistributionAddUpdatePopupStructure,
    IEDistributionDeletePopupStructure
} from "../../_models";
import { APP_CONFIGS } from "../../_constants";
import { utilFunctions } from "../../_helpers";
import { IEDistributionFiltersStructure } from "../../_models";

const IEManagementPage = () => {
    const INITIAL_STATE = {
        addUpdateIEDistributionPopup: false,
        deleteIEDistributionPopup: false
    }
    const INITIAL_FILTER_STATE: IEDistributionFiltersStructure = {
        plantId: { value: "", validator: 'text', isRequired: true, error: null, disabled: false },
        plantCode: { value: "", validator: 'text', isRequired: true, error: null, disabled: false },
        factoryId: { value: "", validator: 'text', isRequired: true, error: null, disabled: false },
        factoryCode: { value: "", validator: 'text', isRequired: true, error: null, disabled: false },
        perWeekHours: { value: null, validator: 'number', isRequired: true, error: null, disabled: false }
    }

    const ADD_UPDATE_POPUP_INITIAL_STATE: IEDistributionAddUpdatePopupStructure = {
        id: { value: null, validator: 'number', isRequired: false, error: null, disabled: false },
        vsmCode: { value: '', validator: 'text', isRequired: true, error: null, disabled: false },
        module: { value: '', validator: 'text', isRequired: true, error: null, disabled: false },
        ieName: { value: '', validator: 'text', isRequired: true, error: null, disabled: false },
        emailAddress: { value: '', validator: 'email', isRequired: true, error: null, disabled: false },
        shift: { value: '', validator: 'text', isRequired: true, error: null, disabled: false }
    }

    const DELETE_POPUP_INITIAL_STATE: IEDistributionDeletePopupStructure = {
        id: { value: null, validator: 'number', isRequired: true, error: null, disabled: false },
        ieName: { value: "", validator: 'text', isRequired: false, error: null, disabled: false },
        isDeleteAll: { value: false, validator: 'boolean', isRequired: true, error: null, disabled: false }
    }

    const dispatch = useDispatch();
    const [ieDistributionMgtPageState, setIEDistributionMgtPageState] = useState(INITIAL_STATE);
    const [ieDistributionAddUpdatePopupData, setIEDistributionAddUpdatePopupData] = useState<IEDistributionAddUpdatePopupStructure>(ADD_UPDATE_POPUP_INITIAL_STATE);
    const [ieDistributionDeletePopupData, setIEDistributionDeletePopupData] = useState<IEDistributionDeletePopupStructure>(DELETE_POPUP_INITIAL_STATE);
    const [filterState, setFilterState] = useState<IEDistributionFiltersStructure>(INITIAL_FILTER_STATE);
    const [plants, setPlants] = useState([]);
    const [factories, setFactories] = useState([])
    const ieDistributionListState = useSelector((state: any) => state.ieDistributions.ieDistributionList)
    const shiftListState = useSelector((state: any) => state.shifts.shiftList);

    useEffect(() => {
        setPlants(authorizationService.getAuthorizedUserPlants())
        dispatch(shiftActions.getAllShiftList(
            () => { },
            (error) => dispatch(alertActions.triggerAlert(true, error, 'error'))
        ))
    }, [])

    const getFilterObj = (): IEDistributionListFilterStructure => {
        return {
            factoryCode: filterState.factoryCode.value,
            plantCode: filterState.plantCode.value,
            perWeekHours: filterState.perWeekHours.value
        }
    }

    // handle filter clear event
    const onFilterClear = () => {
        setFilterState(INITIAL_FILTER_STATE)
        setFactories([])
        dispatch(ieDistributionActions.clearIEDistributionList())
    }

    const onOpenIEDistribution = async () => {
        const [validatedData, isValid]: any = await utilFunctions.validateFormData(filterState)
        setFilterState({ ...validatedData })
        if (isValid) {
            setIEDistributionAddUpdatePopupData(ADD_UPDATE_POPUP_INITIAL_STATE);
            toggleAddUpdateIEDistributionPopup(true, null)
        }
    }

    const onAddUpdatePopupChange = (property, value) => {
        setIEDistributionAddUpdatePopupData({
            ...ieDistributionAddUpdatePopupData,
            [property]: {
                ...ieDistributionAddUpdatePopupData[property],
                value: value,
                error: null
            }
        })
    }

    const onDeletePopupChange = (event) => {
        setIEDistributionDeletePopupData({
            ...ieDistributionDeletePopupData,
            isDeleteAll: {
                ...ieDistributionDeletePopupData.isDeleteAll,
                value: event.target.value == "ALL",
                error: null
            }
        })
    }

    const onAddUpdatePopupClear = () => {
        setIEDistributionAddUpdatePopupData(ADD_UPDATE_POPUP_INITIAL_STATE);
    }

    // handle add user popup toggle
    const toggleAddUpdateIEDistributionPopup = (status: boolean, id: number) => {
        setIEDistributionAddUpdatePopupData(ADD_UPDATE_POPUP_INITIAL_STATE);
        setIEDistributionMgtPageState({
            ...ieDistributionMgtPageState,
            addUpdateIEDistributionPopup: status
        });
    };

    // handle add user popup toggle
    const toggleDeleteIEDistributionPopup = (status: boolean, id: number) => {
        setIEDistributionDeletePopupData(DELETE_POPUP_INITIAL_STATE);
        setIEDistributionMgtPageState({
            ...ieDistributionMgtPageState,
            deleteIEDistributionPopup: status,
        });
    };

    // Handle plant select dropdown change event
    const onPlantChange = (value) => {
        const plantIndex = plants.map(p => p.id).indexOf(value)
        setFilterState({
            ...filterState,
            plantId: { ...filterState.plantId, value: value },
            plantCode: { ...filterState.plantCode, value: plants[plantIndex].code },
            factoryId: { ...filterState.factoryId, value: '', error: null },
            factoryCode: { ...filterState.factoryCode, value: '', error: null },
            perWeekHours: { ...filterState.perWeekHours, value: null, error: null }
        })
        setFactories(plants[plantIndex].factories)
    }

    // Handle per week hour change
    const onPerWeekHourChange = (value) => {
        setFilterState({
            ...filterState,
            perWeekHours: { ...filterState.perWeekHours, value: Math.abs(parseFloat(value.toString())) }
        })
    }

    // Handle factory select dropdown change event
    const onFactoryChange = (value) => {
        const factoryIndex = factories.map(f => f.id).indexOf(value)
        const filters: IEDistributionListFilterStructure = {
            factoryCode: factories[factoryIndex].code,
            plantCode: filterState.plantCode.value,
            perWeekHours: filterState.perWeekHours.value
        }
        dispatch(ieDistributionActions.getIEDistributionList(filters,
            (res) =>
                setFilterState({
                    ...filterState,
                    factoryId: { ...filterState.factoryId, value: value },
                    factoryCode: { ...filterState.factoryCode, value: factories[factoryIndex].code },
                    perWeekHours: { ...filterState.perWeekHours, value: res[0]?.perWeekHours },
                })
            ,
            (error) => {
                dispatch(alertActions.triggerAlert(true, error, 'error'))
            }));
    }

    const initIEDistributionDelete = async (popupStatus: boolean, id: number, name: string) => {
        setIEDistributionDeletePopupData({
            ...ieDistributionDeletePopupData,
            id: { ...ieDistributionDeletePopupData.id, value: id },
            ieName: { ...ieDistributionDeletePopupData.ieName, value: name }
        });
        setIEDistributionMgtPageState({
            ...ieDistributionMgtPageState,
            deleteIEDistributionPopup: popupStatus,
        });
    }

    const initIEDistributionEdit = async (popupStatus: boolean, id: number) => {
        onAddUpdatePopupClear()
        const [validatedData, isValid]: any = await utilFunctions.validateFormData(filterState)
        setFilterState({ ...validatedData })
        if (isValid) {
            dispatch(ieDistributionActions.getIEDistribution(id,
                (res) => {
                    if (res) {
                        setIEDistributionAddUpdatePopupData({
                            ...ieDistributionAddUpdatePopupData,
                            shift: { ...ieDistributionAddUpdatePopupData.shift, value: res?.shift?.trim(), error: null },
                            module: { ...ieDistributionAddUpdatePopupData.module, value: res?.module?.trim(), error: null },
                            vsmCode: { ...ieDistributionAddUpdatePopupData.vsmCode, value: res?.vsmCode?.trim(), error: null },
                            ieName: { ...ieDistributionAddUpdatePopupData.ieName, value: res?.ieName?.trim(), error: null },
                            emailAddress: { ...ieDistributionAddUpdatePopupData.emailAddress, value: res?.emailAddress?.trim(), error: null },
                            id: { ...ieDistributionAddUpdatePopupData.id, value: id, error: null }
                        })
                        setIEDistributionMgtPageState({
                            ...ieDistributionMgtPageState,
                            addUpdateIEDistributionPopup: popupStatus
                        });
                    }
                },
                (error) => {
                    dispatch(alertActions.triggerAlert(true, error, 'error'))
                }))
        }
    }

    const onDeleteIEDistribution = async () => {
        const [validatedData, isValid]: any = await utilFunctions.validateFormData(ieDistributionDeletePopupData);
        setIEDistributionDeletePopupData(validatedData);
        const payload: DeleteIEDistributionPayloadStructure = {
            id: ieDistributionDeletePopupData.id.value,
            isDeleteAll: ieDistributionDeletePopupData.isDeleteAll.value
        }
        if (isValid) {
            dispatch(ieDistributionActions.deleteIEDistribution(
                payload,
                (data, responseMessage) => {
                    setIEDistributionMgtPageState({
                        ...ieDistributionMgtPageState,
                        deleteIEDistributionPopup: false,
                    });
                    setIEDistributionDeletePopupData(DELETE_POPUP_INITIAL_STATE)
                    dispatch(alertActions.triggerAlert(true, responseMessage, 'success'))
                    dispatch(ieDistributionActions.getIEDistributionList(getFilterObj(),
                        (res) => { },
                        (error) => {
                            dispatch(alertActions.triggerAlert(true, error, 'error'))
                        }));
                },
                (error) => {
                    dispatch(alertActions.triggerAlert(true, error, 'error'))
                }))
        }
    }

    const updatePerWeekHours = async () => {
        const [validatedData, isValid]: any = await utilFunctions.validateFormData(filterState)
        setFilterState({ ...validatedData })
        if (isValid) {
            if (ieDistributionListState?.data?.length > 0) {
                dispatch(ieDistributionActions.updatePerWeekHour(getFilterObj(),
                    (data, responseMessage) => {
                        dispatch(alertActions.triggerAlert(true, responseMessage, 'success'))
                        dispatch(ieDistributionActions.getIEDistributionList(getFilterObj(),
                            (res) => { },
                            (error) => {
                                dispatch(alertActions.triggerAlert(true, error, 'error'))
                            }));
                    },
                    (error) => {
                        dispatch(alertActions.triggerAlert(true, error, 'error'))
                    }))
            } else dispatch(alertActions.triggerAlert(true, "No Records. Please Create A New IE Distribution.", 'error'))
        }
    }

    const onAddIEDistribution = async () => {
        const [validatedData, isValid]: any = await utilFunctions.validateFormData(ieDistributionAddUpdatePopupData)
        setIEDistributionAddUpdatePopupData(validatedData);
        if (isValid) {
            const addIEDistributionPayload: AddUpdateIEDistributionPayloadStructure = {
                id: ieDistributionAddUpdatePopupData.id.value,
                emailAddress: ieDistributionAddUpdatePopupData.emailAddress.value,
                ieName: ieDistributionAddUpdatePopupData.ieName.value,
                factoryCode: filterState.factoryCode.value,
                factoryId: Number(filterState.factoryId.value),
                shift: ieDistributionAddUpdatePopupData.shift.value,
                vsmId: null,
                vsmCode: ieDistributionAddUpdatePopupData.vsmCode.value,
                module: ieDistributionAddUpdatePopupData.module.value,
                perWeekHours: filterState.perWeekHours.value,
                isActive: true,
                createdBy: authorizationService.getAuthorizedUser().userEmail
            }
            dispatch(ieDistributionActions.addOrUpdateIEDistribution(addIEDistributionPayload,
                (data, responseMessage) => {
                    onAddUpdatePopupClear();
                    setIEDistributionMgtPageState({ ...ieDistributionMgtPageState, addUpdateIEDistributionPopup: false })
                    dispatch(alertActions.triggerAlert(true, responseMessage, 'success'))
                    dispatch(ieDistributionActions.getIEDistributionList(getFilterObj(),
                        (res) => { },
                        (error) => {
                            dispatch(alertActions.triggerAlert(true, error, 'error'))
                        }));
                }, (error) => {
                    dispatch(alertActions.triggerAlert(true, error, 'error'))
                }))
        }
    }

    return (
        <AppLayout>
            <AppPopup
                open={ieDistributionMgtPageState.addUpdateIEDistributionPopup}
                onClose={(status) => toggleAddUpdateIEDistributionPopup(status, null)}
                maxWidth="sm"
                fullWidth={true}
                title={"Add / Update IE Distribution"}>
                <IEDistributionAddUpdate
                    shifts={shiftListState.data}
                    ieDistributionAddUpdatePopupData={ieDistributionAddUpdatePopupData}
                    onAddUpdatePopupChange={onAddUpdatePopupChange}
                    onAddUpdatePopupClear={onAddUpdatePopupClear}
                    onAddIEDistribution={onAddIEDistribution}
                />
            </AppPopup>
            <AppPopup
                open={ieDistributionMgtPageState.deleteIEDistributionPopup}
                onClose={(status) => toggleDeleteIEDistributionPopup(status, null)}
                maxWidth="sm"
                fullWidth={true}
                title={"Delete Record(s)"}>
                <IEDistributionDelete
                    ieDistributionDeletePopupData={ieDistributionDeletePopupData}
                    onDeleteIEDistribution={onDeleteIEDistribution}
                    onDeletePopupChange={onDeletePopupChange}
                />
            </AppPopup>

            <Grid container className={'appPageHeader'}>
                <Grid container item xs={12} sm={12} md={7} lg={7} justify="flex-start" alignContent="center">
                    <span className={'pageTitle'}>IE <b>Management</b></span>
                </Grid>
            </Grid>

            <div className="appPageContent">
                <IEDistributionListFilters
                    plants={plants}
                    factories={factories}
                    onOpenIEDistribution={onOpenIEDistribution}
                    filterState={filterState}
                    onPlantChange={onPlantChange}
                    onFactoryChange={onFactoryChange}
                    onFilterClear={onFilterClear}
                    onPerWeekHourChange={onPerWeekHourChange}
                    updatePerWeekHours={updatePerWeekHours}
                />
                <IEDistributionList
                    initIEDistributionEdit={(popupStatus, id) => initIEDistributionEdit(popupStatus, id)}
                    initIEDistributionDelete={(popupStatus, id, name) => initIEDistributionDelete(popupStatus, id, name)}
                    isWebview={utilFunctions.isWebView(navigator.userAgent, APP_CONFIGS.TULITA_APP_USER_AGENT)}
                    data={ieDistributionListState.data}
                    isLoading={ieDistributionListState.isLoading}
                />
            </div>
        </AppLayout>
    );
}

export default IEManagementPage;
